import React from 'react';
import { motion } from 'framer-motion';

const CircleOfFifths = ({ selectedKey, hoveredKey, onKeySelect, onKeyHover }) => {
  // Correct Circle of Fifths order starting from C at top (0°)
  const majorKeys = [
    { key: 'C', sharps: 0, angle: 0, color: 'from-red-500 to-red-600' },
    { key: 'G', sharps: 1, angle: 30, color: 'from-pink-500 to-pink-600' },
    { key: 'D', sharps: 2, angle: 60, color: 'from-purple-500 to-purple-600' },
    { key: 'A', sharps: 3, angle: 90, color: 'from-blue-500 to-blue-600' },
    { key: 'E', sharps: 4, angle: 120, color: 'from-cyan-500 to-cyan-600' },
    { key: 'B', sharps: 5, angle: 150, color: 'from-teal-500 to-teal-600' },
    { key: 'G♭', sharps: 6, angle: 180, color: 'from-green-500 to-green-600' },
    { key: 'D♭', flats: 5, angle: 210, color: 'from-lime-500 to-lime-600' },
    { key: 'A♭', flats: 4, angle: 240, color: 'from-yellow-500 to-yellow-600' },
    { key: 'E♭', flats: 3, angle: 270, color: 'from-orange-500 to-orange-600' },
    { key: 'B♭', flats: 2, angle: 300, color: 'from-red-500 to-orange-500' },
    { key: 'F', flats: 1, angle: 330, color: 'from-pink-500 to-red-500' }
  ];

  // Minor keys in the inner circle (relative minors)
  const minorKeys = [
    { key: 'Am', relative: 'C', angle: 0 },
    { key: 'Em', relative: 'G', angle: 30 },
    { key: 'Bm', relative: 'D', angle: 60 },
    { key: 'F♯m', relative: 'A', angle: 90 },
    { key: 'C♯m', relative: 'E', angle: 120 },
    { key: 'G♯m', relative: 'B', angle: 150 },
    { key: 'E♭m', relative: 'G♭', angle: 180 },
    { key: 'B♭m', relative: 'D♭', angle: 210 },
    { key: 'Fm', relative: 'A♭', angle: 240 },
    { key: 'Cm', relative: 'E♭', angle: 270 },
    { key: 'Gm', relative: 'B♭', angle: 300 },
    { key: 'Dm', relative: 'F', angle: 330 }
  ];

  const getColorFromGradient = (gradientClass, position) => {
    const colorMap = {
      'red': position === 'start' ? '#ef4444' : '#dc2626',
      'pink': position === 'start' ? '#ec4899' : '#db2777',
      'purple': position === 'start' ? '#8b5cf6' : '#7c3aed',
      'blue': position === 'start' ? '#3b82f6' : '#2563eb',
      'cyan': position === 'start' ? '#06b6d4' : '#0891b2',
      'teal': position === 'start' ? '#14b8a6' : '#0d9488',
      'green': position === 'start' ? '#10b981' : '#059669',
      'lime': position === 'start' ? '#84cc16' : '#65a30d',
      'yellow': position === 'start' ? '#eab308' : '#ca8a04',
      'orange': position === 'start' ? '#f97316' : '#ea580c'
    };
    
    const colorName = gradientClass.split('-')[1];
    return colorMap[colorName] || colorMap['red'];
  };

  const createKeySegment = (keyData, isMinor = false) => {
    const radius = isMinor ? 120 : 180;
    const innerRadius = isMinor ? 80 : 120;
    
    // Convert angle to radians and adjust for SVG coordinate system
    const angleRad = (keyData.angle - 90) * (Math.PI / 180);
    const nextAngleRad = (keyData.angle + 30 - 90) * (Math.PI / 180);
    
    // Calculate outer arc points
    const x1 = Math.cos(angleRad) * radius;
    const y1 = Math.sin(angleRad) * radius;
    const x2 = Math.cos(nextAngleRad) * radius;
    const y2 = Math.sin(nextAngleRad) * radius;
    
    // Calculate inner arc points
    const x3 = Math.cos(nextAngleRad) * innerRadius;
    const y3 = Math.sin(nextAngleRad) * innerRadius;
    const x4 = Math.cos(angleRad) * innerRadius;
    const y4 = Math.sin(angleRad) * innerRadius;
    
    // Create path for the segment
    const pathData = `M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 0 0 ${x4} ${y4} Z`;
    
    // FIXED: Calculate text position at the CENTER of each segment
    // Add 15 degrees to center the text in the middle of the 30-degree segment
    const textRadius = (radius + innerRadius) / 2;
    const centerAngleRad = (keyData.angle + 15 - 90) * (Math.PI / 180); // Add 15° and adjust for SVG
    const textX = Math.cos(centerAngleRad) * textRadius;
    const textY = Math.sin(centerAngleRad) * textRadius;
    
    const isSelected = selectedKey === keyData.key || hoveredKey === keyData.key;
    const gradientId = `gradient-${keyData.key.replace(/[♯♭]/g, '')}-${isMinor ? 'minor' : 'major'}`;
    
    const startColor = isMinor ? '#6366f1' : getColorFromGradient(keyData.color, 'start');
    const endColor = isMinor ? '#4f46e5' : getColorFromGradient(keyData.color, 'end');

    return (
      <g key={keyData.key}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={startColor} stopOpacity={isSelected ? 1 : 0.8} />
            <stop offset="100%" stopColor={endColor} stopOpacity={isSelected ? 1 : 0.9} />
          </linearGradient>
        </defs>
        <motion.path
          d={pathData}
          fill={`url(#${gradientId})`}
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="1"
          className="cursor-pointer"
          whileHover={{ scale: 1.05 }}
          animate={{
            scale: isSelected ? 1.05 : 1,
            filter: isSelected ? 'brightness(1.2)' : 'brightness(1)'
          }}
          onClick={() => onKeySelect(keyData.key)}
          onMouseEnter={() => onKeyHover(keyData.key)}
          onMouseLeave={() => onKeyHover(null)}
        />
        <text
          x={textX}
          y={textY}
          textAnchor="middle"
          dominantBaseline="middle"
          className={`font-bold pointer-events-none select-none ${isMinor ? 'text-sm' : 'text-lg'} fill-white`}
          style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
        >
          {keyData.key}
        </text>
      </g>
    );
  };

  return (
    <div className="flex flex-col items-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="relative"
      >
        <svg width="400" height="400" viewBox="-200 -200 400 400" className="drop-shadow-2xl">
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <radialGradient id="centerGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(99,102,241,0.9)" />
              <stop offset="100%" stopColor="rgba(79,70,229,1)" />
            </radialGradient>
          </defs>

          {/* Major keys outer ring */}
          {majorKeys.map(key => createKeySegment(key, false))}

          {/* Minor keys inner ring */}
          {minorKeys.map(key => createKeySegment(key, true))}

          {/* Center circle */}
          <circle
            cx="0"
            cy="0"
            r="80"
            fill="url(#centerGradient)"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="2"
            filter="url(#glow)"
          />
          <text
            x="0"
            y="-10"
            textAnchor="middle"
            className="text-lg font-bold fill-white"
            style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
          >
            CIRCLE
          </text>
          <text
            x="0"
            y="10"
            textAnchor="middle"
            className="text-lg font-bold fill-white"
            style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
          >
            OF FIFTHS
          </text>
        </svg>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-6 text-center"
      >
        <p className="text-gray-300 text-sm">
          Click or hover over any key to explore its properties
        </p>
      </motion.div>
    </div>
  );
};

export default CircleOfFifths;